/**
 * 1.7.9 — the month, three ways (D-1.7.9-6): one list for the large-print page, for the clipboard and for the email
 * to one person. Each event is one line — day and date · time · title · place · one line of description — and the
 * three outputs are made from that same line, so what is printed, what is pasted and what is emailed never differ.
 *
 * Pure: nothing here reads a list, and nothing here names a person.
 */
import { clock, dayLine, monthAnnouncement, monthName, sortMonthLines, type MonthLine } from './event-calendar';
import type { EventView } from './events';

export type MonthLang = 'en' | 'es';

/** One event of the month as the three outputs need it: the calendar's line, and what it is about. */
export interface MonthEntry extends MonthLine {
  description: string;
}

/** The large-print standard the office uses for tenants: 18 pt body, 24 pt headings. */
export const LARGE_PRINT = { body: 18, heading: 24 } as const;
/** How long the one line of description may be (it is cut at a word, with an ellipsis). */
export const DESCRIPTION_MAX = 110;

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The first line of a description, on one line: line breaks and runs of spaces become one space; a long one is cut at a word. */
export function oneLine(text: string | null | undefined, max: number = DESCRIPTION_MAX): string {
  const first =
    String(text ?? '')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((l) => l.replace(/\s+/g, ' ').trim())
      .find(Boolean) ?? '';
  if (first.length <= max) return first;
  const cut = first.slice(0, max);
  const at = cut.lastIndexOf(' ');
  return `${(at > max * 0.5 ? cut.slice(0, at) : cut).replace(/[\s.,;:·–—-]+$/, '')}…`;
}

/** "2:00 – 3:00 PM" (the half of the day said once when both ends share it); "2:00 PM" with no end; nothing with no time. */
export function timeRange(e: Pick<MonthLine, 'minutes' | 'endMinutes'>): string {
  if (e.minutes === null) return '';
  const from = clock(e.minutes);
  if (e.endMinutes === null || e.endMinutes <= e.minutes) return from;
  const to = clock(e.endMinutes);
  return from.slice(-2) === to.slice(-2) ? `${from.slice(0, -3)} – ${to}` : `${from} – ${to}`;
}

/** The parts of an event's line, in order: day and date · time · title · place · one line of description (what is empty is left out). */
export function monthEntryParts(e: MonthEntry, lang: MonthLang): string[] {
  return [capital(dayLine(e.dayKey, lang)), timeRange(e), e.name.replace(/\s+/g, ' ').trim(), e.place.replace(/\s+/g, ' ').trim(), oneLine(e.description)].filter(Boolean);
}

/** One event, one line: "Thursday, October 29 · 2:00 – 3:00 PM · Tech Discussion · Auditorium · Bring your phone." */
export const monthEntryLine = (e: MonthEntry, lang: MonthLang): string => monthEntryParts(e, lang).join(' · ');

/** The month's lines, in the order of the calendar. */
export const monthList = (entries: readonly MonthEntry[], lang: MonthLang): string[] => sortMonthLines(entries).map((e) => monthEntryLine(e, lang));

const HEADING = {
  en: { month: (m: string) => `Events for the month of ${m}`, one: 'An event at Selis Manor', none: 'Nothing is on the calendar yet.', upcoming: 'Upcoming events at Selis Manor' },
  es: { month: (m: string) => `Eventos del mes de ${m}`, one: 'Un evento en Selis Manor', none: 'Todavía no hay nada en el calendario.', upcoming: 'Próximos eventos en Selis Manor' },
} as const;

/** 1.8.2 (D-1.8.2-4): the heading over "All upcoming events" — every event from today on, whatever its month. */
export const upcomingHeading = (lang: MonthLang): string => HEADING[lang].upcoming;

const UPCOMING_EMAIL = {
  en: { intro: 'Here are the events coming up at Selis Manor.', outro: 'Questions? Call the office.' },
  es: { intro: 'Estos son los próximos eventos en Selis Manor.', outro: '¿Preguntas? Llame a la oficina.' },
} as const;

/** 1.8.2: the email of all upcoming events to one person — the same lines, its own subject and first and last lines. */
export function upcomingEmail(entries: readonly MonthEntry[], lang: MonthLang): { subject: string; body: string } {
  const list = monthList(entries, lang)
    .map((l) => `- ${l}`)
    .join('\n');
  const w = UPCOMING_EMAIL[lang];
  return { subject: HEADING[lang].upcoming, body: [w.intro, list || HEADING[lang].none, w.outro].join('\n\n') };
}

/** "Events for the month of October 2026" / "Eventos del mes de octubre de 2026"; `one`: the heading over a single event. */
export function monthHeading(month: string, lang: MonthLang, one = false): string {
  if (one) return HEADING[lang].one;
  const name = monthName(month, lang);
  return HEADING[lang].month(lang === 'es' ? name.replace(/ (\d{4})$/, ' de $1') : name);
}
export const monthNothing = (lang: MonthLang): string => HEADING[lang].none;

/**
 * Copy as text: the same list as plain text — one event per line, a blank line between, no letterhead and no
 * heading (it is pasted onto the office's own designed page).
 */
export const monthText = (entries: readonly MonthEntry[], lang: MonthLang): string => monthList(entries, lang).join('\n\n');

const ONE_EVENT = {
  en: { intro: 'Here is an event coming up at Selis Manor.', outro: 'Questions? Call the office.' },
  es: { intro: 'Este es un evento próximo en Selis Manor.', outro: '¿Preguntas? Llame a la oficina.' },
} as const;

/**
 * Email the month to a person: the month's announcement wording (`monthAnnouncement` — its subject, its first and
 * last lines) around the same list. `one`: a single event, under its own name.
 */
export function monthEmail(month: string, entries: readonly MonthEntry[], lang: MonthLang, one = false): { subject: string; body: string } {
  const list = monthList(entries, lang)
    .map((l) => `- ${l}`)
    .join('\n');
  if (one) {
    const w = ONE_EVENT[lang];
    return { subject: entries[0]?.name.trim() || HEADING[lang].one, body: [w.intro, list || HEADING[lang].none, w.outro].join('\n\n') };
  }
  const a = monthAnnouncement(month, entries);
  const parts = a.body[lang].split('\n\n');
  return { subject: a.subject[lang], body: [parts[0], list || HEADING[lang].none, parts[parts.length - 1]].join('\n\n') };
}

/** An event as the screens hold it, as an entry of the month. */
export const entryOfEvent = (e: Pick<EventView, 'eventId' | 'dayKey' | 'minutes' | 'endMinutes' | 'name' | 'place' | 'description' | 'cal'>): MonthEntry => ({ eventId: e.eventId, dayKey: e.dayKey, minutes: e.minutes, endMinutes: e.endMinutes, name: e.name, kind: e.cal?.kind ?? '', place: e.place, description: e.description ?? '' });

/** What the screens list of the month: every event of it that is not cancelled (a cancelled event is not announced, printed or emailed). */
export const monthEntriesOf = (events: readonly Pick<EventView, 'eventId' | 'dayKey' | 'minutes' | 'endMinutes' | 'name' | 'place' | 'description' | 'cal' | 'status'>[], month: string): MonthEntry[] =>
  sortMonthLines(events.filter((e) => e.dayKey.startsWith(month) && e.status !== 'Cancelled').map(entryOfEvent));

/** 1.8.2: what the screens list as all upcoming events — every event from today on that is not cancelled, in calendar order. */
export const upcomingEntriesOf = (events: readonly Pick<EventView, 'eventId' | 'dayKey' | 'minutes' | 'endMinutes' | 'name' | 'place' | 'description' | 'cal' | 'status'>[], todayKey: string): MonthEntry[] => {
  const seen = new Set<string>();
  return sortMonthLines(events.filter((e) => e.dayKey >= todayKey && e.status !== 'Cancelled' && !seen.has(e.eventId) && seen.add(e.eventId)).map(entryOfEvent));
};

/* ------------------------------------------------------------------------------------------------
 * Email the month to a person: who can be found, what the press needs, what is kept
 * ---------------------------------------------------------------------------------------------- */

/** A tenant found by name or apartment, and whether the list can be emailed to them. */
export interface MonthPerson {
  tenantId: string;
  name: string;
  unit: string;
  /** `yes`: an email on file the office may write to · `no-email`: none (print it for them) · `stopped`: they asked for no event emails. */
  canEmail: 'yes' | 'no-email' | 'stopped';
  /** The language the list goes in (the person's own). */
  lang: MonthLang;
}

/** "Sent to 703 · Oct 9": one line a press, kept in the app's own file for the month (`_app/events/month-sent/<yyyyMM>.json`). */
export interface MonthSent {
  /** When it was queued. */
  at: string;
  by: string;
  tenantId: string;
  unit: string;
  lang: MonthLang;
  /** The one event it was about, or null for the whole month. */
  eventId: string | null;
  /** 1.8.2: all upcoming events were sent (filed under the month it was sent in). */
  upcoming?: boolean;
  communicationId: string | null;
  /** The office took it back in the Outbox before it left. */
  cancelled?: boolean;
}

export interface MonthEmailResult {
  queued: number;
  sendAt: string;
  lang: MonthLang;
  /** Words to show and announce. */
  message: string;
  sent: MonthSent[];
}
